<template>
    <div ref="scroller" class="listener-playlists">
        <section class="listener-playlists__hero">
            <div class="listener-playlists__header">
                <div>
                    <div class="listener-playlists__eyebrow">{{ $tt('播放列表') }}</div>
                    <h1 class="listener-playlists__title">{{ pageTitle }}</h1>
                </div>
                <div class="listener-playlists__badge">{{ playlists.length }}</div>
            </div>

            <div class="listener-playlists__tabs">
                <button
                    v-for="{ value, text } in options"
                    :key="value"
                    :class="['listener-playlists__tab', sortValue === value ? 'is-active' : '']"
                    @click="switchSort(value)"
                >
                    {{ text }}
                </button>
            </div>

            <div class="listener-playlists__summary">
                <div class="listener-playlists__summary-kicker">{{ $tt('歌单入口') }}</div>
                <div class="listener-playlists__summary-title">{{ summaryTitle }}</div>
                <div class="listener-playlists__summary-sub">{{ summaryText }}</div>
                <div v-if="playlists.length" class="listener-playlists__actions">
                    <button class="listener-playlists__action listener-playlists__action--primary" @click="openPlaylist(playlists[0])">
                        <svg viewBox="0 0 24 24" fill="currentColor">
                            <path d="M4 6.75A1.75 1.75 0 0 1 5.75 5h12.5A1.75 1.75 0 0 1 20 6.75v10.5A1.75 1.75 0 0 1 18.25 19H5.75A1.75 1.75 0 0 1 4 17.25zm3.75 1.5a.75.75 0 0 0-.75.75v6a.75.75 0 0 0 1.14.64l4.5-3a.75.75 0 0 0 0-1.28l-4.5-3a.75.75 0 0 0-.39-.11z"></path>
                        </svg>
                        <span>{{ $tt('打开第一份') }}</span>
                    </button>
                    <button class="listener-playlists__action" @click="playNow(playlists[0].id)">
                        <svg viewBox="0 0 24 24" fill="currentColor">
                            <path d="M8 5.14v13.72a1 1 0 0 0 1.5.86l10-6.86a1 1 0 0 0 0-1.72l-10-6.86A1 1 0 0 0 8 5.14z"></path>
                        </svg>
                        <span>{{ $tt('直接播放') }}</span>
                    </button>
                </div>
            </div>
        </section>

        <section v-if="playlists.length" class="listener-playlists__section">
            <div class="listener-playlists__list">
                <article
                    v-for="item in playlists"
                    :key="item.id"
                    class="listener-playlists__row"
                >
                    <button class="listener-playlists__main" @click="openPlaylist(item)">
                        <div class="listener-playlists__cover-wrap">
                            <img
                                :src="item.image || '/static/dist/img/album_null.jpg'"
                                :alt="item.name"
                                class="listener-playlists__cover"
                            >
                            <div class="listener-playlists__cover-badge">
                                {{ item.trackCount || 0 }}
                            </div>
                        </div>
                        <div class="listener-playlists__meta">
                            <div class="listener-playlists__name">{{ item.name }}</div>
                            <div class="listener-playlists__sub">
                                {{ playlistSubText(item) }}
                            </div>
                            <div v-if="item.comment" class="listener-playlists__comment">
                                {{ item.comment }}
                            </div>
                        </div>
                    </button>
                    <button class="listener-playlists__play" @click.stop="playNow(item.id)">
                        <svg viewBox="0 0 24 24" fill="currentColor">
                            <path d="M8 5.14v13.72a1 1 0 0 0 1.5.86l10-6.86a1 1 0 0 0 0-1.72l-10-6.86A1 1 0 0 0 8 5.14z"></path>
                        </svg>
                    </button>
                </article>
            </div>
        </section>

        <section v-else-if="!loading" class="listener-playlists__empty">
            <div class="listener-playlists__empty-icon">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8">
                    <path d="M5 7.5h14M5 12h14M5 16.5h8"></path>
                    <path d="M17 16.25V10.5l4 2.875z"></path>
                </svg>
            </div>
            <h2>{{ $tt('还没有播放列表') }}</h2>
            <p>{{ $tt('等你把常听的旋律整理成一份清单，再从这里继续聆听。') }}</p>
        </section>

        <section v-if="loading" class="listener-playlists__state">
            <div class="listener-playlists__loading"></div>
            <p>{{ $tt('正在加载...') }}</p>
        </section>
    </div>
</template>

<script>
export default {
    name: 'ListenerPlaylistLibrary',
    data() {
        return {
            loading: false,
            playlists: [],
            options: [
                { text: this.$tt('最近添加'), value: 'recently-added' },
                { text: this.$tt('A-Z排序'), value: 'a-z' }
            ]
        }
    },
    computed: {
        sortValue() {
            return this.$route.params.sort || 'recently-added'
        },
        pageTitle() {
            const current = this.options.find(item => item.value === this.sortValue)
            return current ? current.text : this.$tt('我的歌单')
        },
        summaryTitle() {
            if (this.sortValue === 'a-z') {
                return this.$tt('按名称浏览你整理好的歌单')
            }
            return this.$tt('最近整理过的歌单都在这里')
        },
        summaryText() {
            if (this.sortValue === 'a-z') {
                return this.$tt('适合按名字快速找回某一份收藏，不用一路往下翻。')
            }
            return this.$tt('从刚导入、刚创建到最近更新的清单，都可以在这里继续播放。')
        }
    },
    watch: {
        sortValue() {
            this.fetchPlaylists()
            this.resetScroll()
        }
    },
    created() {
        this.fetchPlaylists()
    },
    methods: {
        async fetchPlaylists() {
            this.loading = true
            try {
                this.playlists = await this.$api.subsonic.getPlaylists(this.sortValue)
            } finally {
                this.loading = false
            }
        },
        resetScroll() {
            this.$nextTick(() => {
                if (this.$refs.scroller) {
                    this.$refs.scroller.scrollTop = 0
                }
            })
        },
        switchSort(value) {
            if (value === this.sortValue) {
                return
            }
            this.$router.push({ path: '/playlists/' + value })
        },
        openPlaylist(item) {
            this.$router.push({ name: 'playlist', params: { id: item.id } })
        },
        async playNow(id) {
            const playlist = await this.$api.subsonic.getPlaylist(id)
            return this.$store.dispatch('playTrackList', {
                tracks: playlist.tracks
            })
        },
        playlistSubText(item) {
            const parts = [
                this.$tt('{count} 首歌曲', { count: item.trackCount || 0 })
            ]
            if (item.owner) {
                parts.push(item.owner)
            }
            if (item.isDynamic) {
                parts.push(this.$tt('动态歌单'))
            }
            return parts.join(' · ')
        }
    }
}
</script>

<style scoped>
.listener-playlists {
    height: calc(100dvh - 12.25rem);
    padding: 0.45rem 0.1rem 0.25rem;
    box-sizing: border-box;
    overflow-y: auto;
}

.listener-playlists__hero {
    padding: 0.3rem 0.25rem 0.2rem;
}

.listener-playlists__header {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    gap: 1rem;
    margin-bottom: 1rem;
}

.listener-playlists__eyebrow {
    font-size: 0.85rem;
    font-weight: 700;
    color: #94a3b8;
    letter-spacing: 0.04em;
}

.listener-playlists__title {
    margin-top: 0.2rem;
    font-size: 2rem;
    line-height: 1;
    font-weight: 800;
    color: #111827;
    letter-spacing: -0.04em;
}

.listener-playlists__badge {
    min-width: 2.8rem;
    height: 2.8rem;
    padding: 0 0.8rem;
    border-radius: 999px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    background: rgba(17, 24, 39, 0.95);
    color: #fff;
    font-size: 0.95rem;
    font-weight: 700;
    box-shadow: 0 12px 26px rgba(15, 23, 42, 0.18);
}

.listener-playlists__tabs {
    display: flex;
    gap: 0.55rem;
    overflow-x: auto;
    padding-bottom: 0.15rem;
    scrollbar-width: none;
}

.listener-playlists__tabs::-webkit-scrollbar {
    display: none;
}

.listener-playlists__tab {
    border: 0;
    border-radius: 999px;
    padding: 0.72rem 0.9rem;
    white-space: nowrap;
    background: rgba(255, 255, 255, 0.85);
    color: #64748b;
    font-size: 0.84rem;
    font-weight: 700;
    box-shadow: 0 10px 24px rgba(148, 163, 184, 0.1);
}

.listener-playlists__tab.is-active {
    background: #111827;
    color: #fff;
    box-shadow: 0 16px 30px rgba(15, 23, 42, 0.2);
}

.listener-playlists__summary {
    margin-top: 0.95rem;
    padding: 1rem;
    border-radius: 1.65rem;
    background: linear-gradient(135deg, rgba(17, 24, 39, 0.96), rgba(55, 65, 81, 0.92));
    color: #fff;
    box-shadow: 0 20px 46px rgba(15, 23, 42, 0.16);
}

.listener-playlists__summary-kicker {
    font-size: 0.72rem;
    color: rgba(255, 255, 255, 0.64);
}

.listener-playlists__summary-title {
    margin-top: 0.35rem;
    font-size: 1.08rem;
    font-weight: 700;
    line-height: 1.4;
}

.listener-playlists__summary-sub {
    margin-top: 0.32rem;
    font-size: 0.8rem;
    line-height: 1.55;
    color: rgba(255, 255, 255, 0.68);
}

.listener-playlists__actions {
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 0.65rem;
    margin-top: 0.9rem;
}

.listener-playlists__action {
    border: 0;
    border-radius: 1rem;
    padding: 0.78rem 0.65rem;
    background: rgba(255, 255, 255, 0.12);
    color: #fff;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 0.45rem;
    font-size: 0.82rem;
    font-weight: 700;
}

.listener-playlists__action--primary {
    background: #fff;
    color: #111827;
}

.listener-playlists__action svg {
    width: 1rem;
    height: 1rem;
}

.listener-playlists__section {
    margin-top: 1rem;
    padding: 0 0.25rem 0.9rem;
}

.listener-playlists__list {
    display: flex;
    flex-direction: column;
    gap: 0.78rem;
}

.listener-playlists__row {
    display: flex;
    align-items: center;
    gap: 0.78rem;
    padding: 0.72rem;
    border-radius: 1.45rem;
    background: rgba(255, 255, 255, 0.78);
    box-shadow: 0 12px 28px rgba(148, 163, 184, 0.1);
}

.listener-playlists__main {
    min-width: 0;
    flex: 1;
    display: flex;
    align-items: center;
    gap: 0.82rem;
    border: 0;
    padding: 0;
    background: transparent;
    text-align: left;
}

.listener-playlists__cover-wrap {
    position: relative;
    flex-shrink: 0;
}

.listener-playlists__cover {
    width: 4.7rem;
    height: 4.7rem;
    border-radius: 1.2rem;
    object-fit: cover;
    box-shadow: 0 14px 26px rgba(148, 163, 184, 0.18);
}

.listener-playlists__cover-badge {
    position: absolute;
    right: 0.45rem;
    bottom: 0.45rem;
    min-width: 1.55rem;
    height: 1.55rem;
    padding: 0 0.35rem;
    border-radius: 999px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    background: rgba(17, 24, 39, 0.92);
    color: #fff;
    font-size: 0.68rem;
    font-weight: 700;
}

.listener-playlists__meta {
    min-width: 0;
    flex: 1;
}

.listener-playlists__name,
.listener-playlists__sub,
.listener-playlists__comment {
    overflow: hidden;
    text-overflow: ellipsis;
}

.listener-playlists__name {
    white-space: nowrap;
    font-size: 0.98rem;
    font-weight: 700;
    color: #111827;
}

.listener-playlists__sub {
    margin-top: 0.26rem;
    white-space: nowrap;
    font-size: 0.78rem;
    color: #64748b;
}

.listener-playlists__comment {
    margin-top: 0.36rem;
    display: -webkit-box;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
    font-size: 0.76rem;
    line-height: 1.45;
    color: #94a3b8;
}

.listener-playlists__play {
    width: 2.7rem;
    height: 2.7rem;
    border: 0;
    border-radius: 999px;
    flex-shrink: 0;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    background: rgba(17, 24, 39, 0.92);
    color: #fff;
    box-shadow: 0 12px 22px rgba(15, 23, 42, 0.18);
}

.listener-playlists__play svg {
    width: 1rem;
    height: 1rem;
}

.listener-playlists__empty,
.listener-playlists__state {
    padding: 2.4rem 1rem 1rem;
    text-align: center;
    color: #64748b;
}

.listener-playlists__empty h2 {
    margin-top: 0.85rem;
    font-size: 1.15rem;
    font-weight: 700;
    color: #111827;
}

.listener-playlists__empty p,
.listener-playlists__state p {
    margin-top: 0.45rem;
    font-size: 0.84rem;
    line-height: 1.6;
}

.listener-playlists__empty-icon {
    width: 3.6rem;
    height: 3.6rem;
    margin: 0 auto;
    border-radius: 1.3rem;
    display: flex;
    align-items: center;
    justify-content: center;
    background: rgba(255, 255, 255, 0.84);
    color: #94a3b8;
    box-shadow: 0 12px 26px rgba(148, 163, 184, 0.12);
}

.listener-playlists__empty-icon svg {
    width: 1.7rem;
    height: 1.7rem;
}

.listener-playlists__loading {
    width: 2rem;
    height: 2rem;
    margin: 0 auto;
    border-radius: 999px;
    border: 2px solid rgba(148, 163, 184, 0.24);
    border-top-color: #111827;
    animation: listener-playlists-spin 0.8s linear infinite;
}

@keyframes listener-playlists-spin {
    to {
        transform: rotate(360deg);
    }
}
</style>
